'use client';

/**
 * Teacher assignments management page.
 *
 * Module: app/assignments/page
 * Layer:  page (client component)
 *
 * Purpose:
 *     The teacher view of `/assignments`: a header with a (stubbed) create
 *     action, a filter bar (classroom, status, search), and a table of
 *     assignments with submission/grading progress that links to each
 *     assignment's detail page. Renders loading and empty states. Filtering
 *     and data loading live in use-assignments-list so the page stays a thin
 *     composition and every function clears the CC<=5 / <=60-line gates.
 *
 * AI context:
 *     - Entry points called by: Next.js App Router for `/assignments`.
 *     - Depends on: use-assignments-list (data + filters), assignment-table
 *       (presentation), Nav, useTranslation.
 *     - Side effects: none directly; the hook owns network I/O + cleanup. The
 *       create button shows a placeholder alert until the create flow lands.
 *     - Invariants: every visible string is from the i18n catalogue.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Assignment rows are validated at the lib boundary; the page renders
 *       only text fields, which React escapes.
 */

import { type ChangeEvent, useCallback } from 'react';
import type React from 'react';
import Nav from '@/components/Nav';
import { type AssignmentStatus, ASSIGNMENT_STATUS_KEY } from '@/lib/assignments';
import { type TranslationKey, useTranslation } from '@/lib/i18n';
import { AssignmentTable } from './assignment-table';
import {
  type AssignmentsListController,
  type ClassroomFilter,
  type StatusFilter,
  useAssignmentsList,
} from './use-assignments-list';

/** The status options rendered in the status dropdown (data-driven). */
const STATUS_OPTIONS: readonly AssignmentStatus[] = [
  'draft',
  'published',
  'archived',
  'closed',
];

/** Header with the page title and the (stubbed) create action. */
function Header(): React.JSX.Element {
  const { t } = useTranslation();
  const onCreate = useCallback((): void => {
    // WHY: the create flow is not built yet; surface a placeholder so the
    // affordance is discoverable without pretending to persist anything.
    globalThis.alert(t('assignments.teacher.createStub'));
  }, [t]);
  return (
    <div className='flex items-center justify-between'>
      <div>
        <h1 className='text-3xl font-bold'>{t('assignments.teacher.title')}</h1>
        <p className='mt-1 opacity-70'>{t('assignments.teacher.subtitle')}</p>
      </div>
      <button
        type='button'
        onClick={onCreate}
        className='rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-foreground/5'
      >
        {t('assignments.teacher.create')}
      </button>
    </div>
  );
}

/** Classroom filter dropdown driven by the loaded classroom ids. */
function ClassroomSelect(props: { readonly ctrl: AssignmentsListController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      const raw = e.target.value;
      const next: ClassroomFilter = raw === 'all' ? 'all' : Number.parseInt(raw, 10);
      ctrl.setClassroomFilter(next);
    },
    [ctrl],
  );
  return (
    <select
      value={ctrl.classroomFilter === 'all' ? 'all' : String(ctrl.classroomFilter)}
      onChange={onChange}
      aria-label={t('assignments.teacher.filterClassroom')}
      className='rounded-lg border border-border bg-background px-3 py-2 text-sm'
    >
      <option value='all'>{t('assignments.teacher.allClassrooms')}</option>
      {ctrl.classroomIds.map((id) => (
        <option key={id} value={String(id)}>
          {t('assignments.teacher.classroomOption', { id })}
        </option>
      ))}
    </select>
  );
}

/** Status filter dropdown. */
function StatusSelect(props: { readonly ctrl: AssignmentsListController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      ctrl.setStatusFilter(e.target.value as StatusFilter);
    },
    [ctrl],
  );
  return (
    <select
      value={ctrl.statusFilter}
      onChange={onChange}
      aria-label={t('assignments.teacher.filterStatus')}
      className='rounded-lg border border-border bg-background px-3 py-2 text-sm'
    >
      <option value='all'>{t('assignments.teacher.allStatuses')}</option>
      {STATUS_OPTIONS.map((status) => (
        <option key={status} value={status}>
          {t(ASSIGNMENT_STATUS_KEY[status] satisfies TranslationKey)}
        </option>
      ))}
    </select>
  );
}

/** The filter bar: search box plus classroom and status dropdowns. */
function FilterBar(props: { readonly ctrl: AssignmentsListController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => ctrl.setSearch(e.target.value),
    [ctrl],
  );
  return (
    <div className='flex flex-col gap-3 md:flex-row md:items-center'>
      <input
        type='search'
        value={ctrl.search}
        onChange={onSearch}
        placeholder={t('assignments.teacher.searchPlaceholder')}
        className='flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm'
      />
      <ClassroomSelect ctrl={ctrl} />
      <StatusSelect ctrl={ctrl} />
    </div>
  );
}

/** Empty-state panel with a clear-filters action. */
function EmptyState(props: { readonly onClear: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='rounded-xl border border-border p-12 text-center'>
      <h2 className='text-lg font-semibold'>{t('assignments.teacher.empty.title')}</h2>
      <p className='mt-2 opacity-70'>{t('assignments.teacher.empty.subtitle')}</p>
      <button
        type='button'
        onClick={props.onClear}
        className='mt-4 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-foreground/5'
      >
        {t('assignments.teacher.empty.clear')}
      </button>
    </div>
  );
}

/** The data-dependent body: loading, empty, or the table. */
function ListBody(props: { readonly ctrl: AssignmentsListController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  if (ctrl.loading) {
    return <p className='py-12 text-center opacity-70'>{t('assignments.teacher.loading')}</p>;
  }
  if (ctrl.assignments.length === 0) {
    return <EmptyState onClear={ctrl.clearFilters} />;
  }
  return <AssignmentTable assignments={ctrl.assignments} />;
}

/**
 * Render the teacher assignments page.
 *
 * @returns The assignments management page element.
 */
export default function AssignmentsPage(): React.JSX.Element {
  const ctrl = useAssignmentsList();
  return (
    <div className='min-h-screen'>
      <Nav />
      <main className='mx-auto max-w-5xl space-y-6 p-8'>
        <Header />
        <FilterBar ctrl={ctrl} />
        <ListBody ctrl={ctrl} />
      </main>
    </div>
  );
}
